const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export function mountReactBitsEffects(root = document) {
	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	const mobile = window.matchMedia("(pointer: coarse), (max-width: 899px)").matches;
	const cleanups = [];

	// Large editorial headings only: page titles and labelled section titles.
	const editorialHeadingSelector = [
		"main h1",
		"main h2[id]",
		"main .solufy-section-head h2",
		"main .solufy-contact-title"
	].join(",");
	const editorialHeadings = [...root.querySelectorAll(editorialHeadingSelector)]
		.filter((heading) => !heading.closest('[data-map-animation="0"]'));

	// Blur Highlight — animate emphasized phrases inside those editorial headings.
	const highlights = editorialHeadings
		.flatMap((heading) => [...heading.querySelectorAll("em")]);
	highlights.forEach((highlight) => highlight.classList.add("solufy-blur-highlight"));

	// Staggered Text — preserve the existing markup while revealing individual words.
	const staggeredHeadings = editorialHeadings.filter((heading) => {
		const hasComplexHeroAnimation = heading.matches(".solufy-hero-headline")
			|| heading.closest(".cd-headline, [data-word-rotator]");
		return !hasComplexHeroAnimation && !heading.dataset.staggerReady;
	});

	staggeredHeadings.forEach((heading) => {
		const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, {
			acceptNode: (node) => {
				if (!node.textContent.trim()) return NodeFilter.FILTER_REJECT;
				// Keep gradient/highlight phrases intact. Splitting them into child spans
				// breaks background-clipped text in Chromium.
				if (node.parentElement?.closest(".solufy-blur-highlight")) {
					return NodeFilter.FILTER_REJECT;
				}
				return NodeFilter.FILTER_ACCEPT;
			}
		});
		const textNodes = [];
		while (walker.nextNode()) textNodes.push(walker.currentNode);

		let wordIndex = 0;
		textNodes.forEach((node) => {
			const fragment = document.createDocumentFragment();
			node.textContent.split(/(\s+)/).forEach((part) => {
				if (!part || /^\s+$/.test(part)) {
					fragment.appendChild(document.createTextNode(part));
					return;
				}
				const word = document.createElement("span");
				word.className = "solufy-stagger-word";
				word.style.setProperty("--stagger-index", Math.min(wordIndex, 14));
				word.textContent = part;
				fragment.appendChild(word);
				wordIndex += 1;
			});
			node.replaceWith(fragment);
		});
		heading.dataset.staggerReady = "true";
		heading.classList.add("solufy-stagger-text");
	});

	const animatedEditorial = [...new Set([...highlights, ...staggeredHeadings])];
	if (animatedEditorial.length) {
		if (reduceMotion || !("IntersectionObserver" in window)) {
			animatedEditorial.forEach((element) => element.classList.add("is-motion-visible"));
		} else {
			const editorialObserver = new IntersectionObserver((entries, observer) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					entry.target.classList.add("is-motion-visible");
					observer.unobserve(entry.target);
				});
			}, { threshold: .24, rootMargin: "0px 0px -8%" });
			animatedEditorial.forEach((element) => editorialObserver.observe(element));
			cleanups.push(() => editorialObserver.disconnect());
		}
	}

	const buttonSelector = [
		".solufy-button", ".solufy-hero-consult-btn", ".button-50", ".solufy-btn-send",
		".newsletter-btn", ".solufy-nav__btn-ghost", ".solufy-megamenu__featured-cta",
		".client-link", ".solufy-industry-link"
	].join(",");
	root.querySelectorAll(buttonSelector).forEach((button) => {
		button.classList.add("solufy-button-fx");
		if (!button.classList.contains("solufy-specular-button") && !button.querySelector(":scope > .solufy-button-shine")) {
			const shine = document.createElement("span");
			shine.className = "solufy-button-shine";
			shine.setAttribute("aria-hidden", "true");
			button.appendChild(shine);
		}
	});

	// Scroll Expand — gives the product film a compact frame that opens as it approaches.
	const video = root.querySelector(".solufy-video-mockup");
	if (video && !reduceMotion && !mobile) {
		let ticking = false;
		const updateVideo = () => {
			const rect = video.getBoundingClientRect();
			const progress = clamp(1 - rect.top / (window.innerHeight * 0.92), 0, 1);
			video.style.setProperty("--scroll-expand", progress.toFixed(3));
			video.style.setProperty("--scroll-inset-y", `${((1 - progress) * 7).toFixed(2)}%`);
			video.style.setProperty("--scroll-inset-x", `${((1 - progress) * 10).toFixed(2)}%`);
			video.style.setProperty("--scroll-scale", (0.82 + progress * 0.18).toFixed(3));
			ticking = false;
		};
		const onScroll = () => {
			if (!ticking) requestAnimationFrame(updateVideo);
			ticking = true;
		};
		updateVideo();
		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onScroll, { passive: true });
		cleanups.push(() => {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onScroll);
		});
	}

	// Variable Proximity — cursor distance subtly changes heading weight/spacing.
	root.querySelectorAll(".solufy-proximity").forEach((element) => {
		const onMove = (event) => {
			const rect = element.getBoundingClientRect();
			const x = event.clientX - (rect.left + rect.width / 2);
			const y = event.clientY - (rect.top + rect.height / 2);
			const influence = 1 - clamp(Math.hypot(x, y) / Math.max(rect.width, 420), 0, 1);
			element.style.setProperty("--proximity", influence.toFixed(3));
		};
		const reset = () => element.style.setProperty("--proximity", "0");
		element.addEventListener("pointermove", onMove);
		element.addEventListener("pointerleave", reset);
		cleanups.push(() => {
			element.removeEventListener("pointermove", onMove);
			element.removeEventListener("pointerleave", reset);
		});
	});

	return () => cleanups.forEach((cleanup) => cleanup());
}
